/**
 * Impersonation banner + Return-to-Super-Admin control.
 *
 * Mounted at the top of every authenticated page. Only renders when the
 * current session is an active super_admin impersonation session. Never
 * displays PII beyond a safely-generated display name.
 */
import React from "react";
import { UserX2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useImpersonationStatus } from "@/lib/useQaAccess";

export function ImpersonationBanner() {
  const status = useImpersonationStatus();

  if (!status.impersonating) return null;

  const handleReturn = async () => {
    try {
      await api.post("/impersonation/end");
      toast.success("Returned to Super Administrator session.");
      // Full reload so every hook re-reads the restored cookie.
      window.location.href = "/admin";
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not end impersonation session.");
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="w-full bg-amber-500 text-ink border-b border-amber-700 px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-[999]"
      style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
    >
      <div className="flex items-center gap-2 text-[12px] uppercase tracking-widest">
        <UserX2 size={14} />
        <span data-testid="impersonation-banner-text">{status.banner_text || "Viewing as user"}</span>
        <span className="opacity-75">· Authorized QA session — Production test access</span>
      </div>
      <button
        type="button"
        onClick={handleReturn}
        data-testid="impersonation-return-btn"
        className="text-[11px] uppercase tracking-widest bg-ink text-ivory px-3 py-1 inline-flex items-center gap-1 hover:bg-black"
      >
        <ArrowLeft size={12} /> Return to Super Admin
      </button>
    </div>
  );
}

export default ImpersonationBanner;
